import { Entity, parseEntityRef } from '@backstage/catalog-model';

export type ApplicationGroupOwnerSubject = {
  type: 'user' | 'group';
  id: string;
  displayRef?: string;
};

export type ApplicationGroupV1Spec = NonNullable<Entity['spec']> & {
  id: string;
  tenant: string;
  description: string;
  owner: string;
  ownerSubject?: ApplicationGroupOwnerSubject;
  stacks: string[];
  lifecycle: 'active' | 'retired';
  provisioningStatus: 'pending' | 'ready' | 'failed';
  createdAt: string;
  updatedAt: string;
};

export type ApplicationGroupV1Entity = Entity & {
  apiVersion: 'aeven.io/v1alpha1';
  kind: 'ApplicationGroup';
  spec: ApplicationGroupV1Spec;
};

function hasQualifiedRef(
  value: unknown,
  kind: 'group' | 'user' | 'stack',
  namespace?: string,
): value is string {
  if (
    typeof value !== 'string' ||
    !value.includes(':') ||
    !value.includes('/')
  ) {
    return false;
  }
  try {
    const ref = parseEntityRef(value);
    return (
      ref.kind.toLocaleLowerCase('en-US') === kind &&
      (namespace === undefined || ref.namespace === namespace)
    );
  } catch {
    return false;
  }
}

export function isApplicationGroupV1Entity(
  entity: Entity,
): entity is ApplicationGroupV1Entity {
  const spec = entity.spec as Partial<ApplicationGroupV1Spec> | undefined;
  const namespace = entity.metadata.namespace;
  return (
    entity.apiVersion === 'aeven.io/v1alpha1' &&
    entity.kind === 'ApplicationGroup' &&
    typeof namespace === 'string' &&
    spec?.tenant === namespace &&
    typeof spec.id === 'string' &&
    typeof spec.description === 'string' &&
    (hasQualifiedRef(spec.owner, 'group') ||
      hasQualifiedRef(spec.owner, 'user')) &&
    Array.isArray(spec.stacks) &&
    spec.stacks.every(ref => hasQualifiedRef(ref, 'stack', namespace)) &&
    (spec.lifecycle === 'active' || spec.lifecycle === 'retired') &&
    ['pending', 'ready', 'failed'].includes(String(spec.provisioningStatus)) &&
    typeof spec.createdAt === 'string' &&
    typeof spec.updatedAt === 'string'
  );
}

/** Stack relations are explicit in the Orchestrator DSO contract. */
export function applicationGroupStackRefs(
  entity: ApplicationGroupV1Entity,
): string[] {
  return entity.spec.stacks;
}
